import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/lib';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import LoadingPage from '../components/LoadingPage';

const InvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [invitation, setInvitation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [accepting, setAccepting] = useState(false);

  // Load invitation details
  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const getInvitationDetails = httpsCallable(functions, 'getInvitationDetails');
        const result = await getInvitationDetails({ token });

        if (result.data) {
          const data = result.data as any;
          if (data.success) {
            setInvitation(data.invitation);
          } else {
            setError('Invitation not found or expired');
          }
        }
      } catch (error: any) {
        console.error('Error loading invitation:', error);
        setError(error.message || 'Failed to load invitation');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      loadInvitation();
    }
  }, [token, authLoading]);

  // Handle invitation acceptance
  const handleAcceptInvitation = async () => {
    if (!token || !user) {
      setError('Unable to accept invitation');
      return;
    }

    setAccepting(true);
    setError('');

    try {
      const acceptInvitation = httpsCallable(functions, 'acceptInvitationFunction');
      const result = await acceptInvitation({ token });

      if (result.data) {
        const data = result.data as any;
        if (data.success) {
          // Navigate to project linking page
          navigate('/linking-project', {
            state: {
              businessIdeaId: data.businessIdeaId,
              message: data.message,
            },
          });
        } else {
          setError('Failed to accept invitation');
        }
      }
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      setError(error.message || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  // Show loading while auth is initializing
  if (authLoading || loading) {
    return (
      <LoadingPage
        title="Loading Invitation..."
        subtitle="Please wait while we verify your invitation"
        size="medium"
      />
    );
  }

  // If user is not authenticated, redirect to login with invitation context
  if (!user) {
    return (
      <Navigate
        to="/login"
        state={{
          invitationToken: token,
          redirectTo: `/invite/${token}`,
        }}
        replace
      />
    );
  }

  // If no token provided, show error
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)',
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%', mx: 'auto' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ fontSize: '4rem', mb: 2 }}>🚀</Box>
            <Typography variant="h4" component="h1" gutterBottom>
              You're invited to collaborate!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Join this exciting project on Orbit
            </Typography>
          </Box>

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={40} />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading invitation details...
              </Typography>
            </Box>
          )}

          {/* Invitation Details */}
          {invitation && !loading && (
            <Box sx={{ mb: 4 }}>
              <Chip
                label={invitation.businessIdeaId ? 'Valid Invitation' : 'Expired Invitation'}
                color={invitation.businessIdeaId ? 'success' : 'error'}
                sx={{ mb: 3 }}
              />

              {invitation.businessIdeaId && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Project Details
                  </Typography>

                  <Box sx={{ textAlign: 'left', mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Invited by:</strong> {invitation.inviterId || 'Team Member'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Project ID:</strong> {invitation.businessIdeaId}
                    </Typography>
                    {invitation.message && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Personal Message:</strong>
                        </Typography>
                        <Typography variant="body2">{invitation.message}</Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Accept Button */}
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleAcceptInvitation}
                    disabled={accepting}
                    startIcon={accepting ? <CircularProgress size={20} /> : <CheckCircle />}
                    sx={{ minWidth: 200 }}
                  >
                    {accepting ? 'Joining Project...' : 'Join Project'}
                  </Button>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    By joining, you'll be able to collaborate on this project and access all shared
                    content.
                  </Typography>
                </>
              )}

              {!invitation.businessIdeaId && (
                <Alert severity="error">
                  This invitation has expired or is no longer valid. Please contact the person who
                  invited you for a new invitation.
                </Alert>
              )}
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Secured by Orbit • Invitation expires in 7 days
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InvitationPage;
